import { useEffect, useState } from 'react';
import { CalendarDays, Check, Crown, Users } from 'lucide-react';
import { fetchPublicPlans } from '../services/api';
import { formatCurrency, getHorizonLabel } from '../utils/planCatalog';

const DISCOUNT = 0.20;

// Tarjeta enfocada en el Oráculo 365 con el descuento del 20% del beneficio
// de prueba. Se usa en la columna derecha de la página "Tu beneficio está
// reservado" (TrialStatusPage).
export default function OracleDiscountCard() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchPublicPlans().then(data => {
      if (cancelled) return;
      if (!data?.success || !Array.isArray(data.plans)) throw new Error('Catálogo no disponible');
      const oracle = data.plans.find(p => p.planCode === 'ORACULO_365');
      if (!oracle) throw new Error('El Oráculo 365 no está disponible por ahora.');
      setPlan(oracle);
    }).catch(() => {
      if (!cancelled) setError('No pudimos cargar la información del Oráculo. Intenta de nuevo.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attempt]);

  if (loading) return <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-xl"><p role="status" className="text-center text-gray-600 py-8">Cargando el Oráculo 365…</p></div>;
  if (error) return <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-xl text-center">
    <p role="alert" className="text-red-700">{error}</p>
    <button type="button" onClick={() => setAttempt(v => v + 1)} className="mt-3 font-semibold text-morado underline">Reintentar</button>
  </div>;
  if (!plan) return null;

  const prices = plan.pricing?.prices || {};
  const promoActive = plan.pricing?.promo?.isActive;
  const baseAnnual = promoActive && prices.promoAnnual ? prices.promoAnnual : prices.annual;
  const discounted = baseAnnual ? Math.round(baseAnnual * (1 - DISCOUNT)) : null;
  const features = [];
  if (plan.icsEnabled === true) features.push('Exportación de calendario (ICS) incluida');
  if (plan.statsEnabled === true) features.push('Estadísticas incluidas');

  return <article className="flex flex-col rounded-2xl border border-[#d4af37]/40 bg-white p-6 sm:p-8 shadow-xl">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-12 h-12 rounded-2xl bg-[#d4af37] flex items-center justify-center text-morado">
        <Crown className="w-6 h-6" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-morado">{plan.displayName}</h2>
        {plan.tagline && <p className="text-sm text-gray-600">{plan.tagline}</p>}
      </div>
    </div>
    {plan.description && <p className="text-sm text-gray-600 whitespace-pre-line">{plan.description}</p>}
    <div className="my-5 rounded-xl bg-morado/5 p-4 space-y-3">
      <p className="flex items-start gap-2 text-sm font-semibold text-morado"><CalendarDays className="w-5 h-5 shrink-0" aria-hidden="true" />{getHorizonLabel(plan)}</p>
      {plan.ninjaIncluded != null && <p className="flex items-start gap-2 text-sm text-morado"><Users className="w-5 h-5 shrink-0" aria-hidden="true" />{plan.ninjaIncluded} {Number(plan.ninjaIncluded) === 1 ? 'perfil adicional Ninja incluido' : 'perfiles adicionales Ninja incluidos'}</p>}
    </div>
    <ul className="space-y-3 flex-1">
      {features.map(feature => <li key={feature} className="flex items-start gap-2 text-sm text-gray-600"><Check className="w-4 h-4 shrink-0 text-morado mt-0.5" aria-hidden="true" /><span>{feature}</span></li>)}
    </ul>
    {baseAnnual && <div className="mt-6 border-t border-morado/10 pt-4">
      <span className="inline-flex items-center rounded-full bg-[#d4af37]/20 px-3 py-1 text-xs font-semibold text-[#8a6d00]">20% de descuento con tu beneficio</span>
      <p className="mt-3 text-morado">
        <span className="mr-2 text-base text-gray-400 line-through">{formatCurrency(baseAnnual)}</span>
        <span className="text-3xl font-bold">{formatCurrency(discounted)}</span>
        <span className="text-sm text-gray-500"> MXN /año</span>
      </p>
      <p className="mt-1 text-xs text-gray-500">Precio del primer año del plan anual aplicando tu código de beneficio.</p>
    </div>}
  </article>;
}
